import { computed, nextTick, ref } from 'vue';

/** Distanza fra il bordo dell'elemento evidenziato e il ritaglio, in pixel. */
const SPOT_PADDING = 8;
/**
 * Oltre questo tempo il bersaglio si considera irraggiungibile. Tenuto corto: e' il
 * tempo in cui resta a schermo il titolo di un passo che non ha ancora nulla da
 * mostrare, e a Vue per ridisegnare ne basta molto meno. I passi che sappiamo
 * condizionati lo abbassano con `timeout`, cosi' l'attesa non si sente.
 */
const TARGET_TIMEOUT_MS = 1200;
/** Intervallo fra un tentativo e l'altro mentre si aspetta il bersaglio. */
const POLL_MS = 50;
/** Spazio da lasciare ai bordi prima di decidere che l'elemento va portato a schermo. */
const SCROLL_MARGIN = 24;
/** Quanto si continua a misurare dopo un cambio di passo, per lasciare finire i ridisegni. */
const SETTLE_MS = 700;
/** Coda di misure dopo l'ultimo scorrimento o ridimensionamento. */
const SETTLE_SCROLL_MS = 150;
/**
 * Tetto assoluto alla misurazione continua.
 *
 * Finche' il bersaglio si muove la finestra di misura si riapre - serve alle
 * transizioni: la `v-dialog` a tutto schermo entra traslata, e chi misura mentre
 * sta salendo illumina un punto fuori schermo e ci resta. Il tetto evita che una
 * pagina con un'animazione perpetua tenga acceso `requestAnimationFrame` per sempre.
 */
const SETTLE_MAX_MS = 3000;

/**
 * Motore del tutorial guidato: tiene il passo corrente e il rettangolo da
 * evidenziare, e lascia al componente il solo compito di disegnarli.
 *
 * Il bersaglio non viene mai memorizzato: le pagine del BO ricostruiscono i propri
 * elementi a ogni ricarica dei dati, quindi un riferimento salvato punterebbe a un
 * nodo staccato dal documento.
 *
 * Forma di un passo:
 * - `key`      identificativo, serve agli attributi aria
 * - `title`    titolo della card
 * - `body`     testo della card
 * - `target`   selettore CSS, oppure funzione che restituisce l'elemento
 * - `before`   preparazione della pagina: deve essere idempotente e valere anche
 *              all'indietro, cosi' ogni passo descrive per intero lo stato che gli
 *              serve senza dipendere da quello che ha lasciato il precedente
 * - `padding`  scostamento del ritaglio dal bordo dell'elemento
 * - `placement` 'auto' | 'bottom' | 'left' | 'right' | 'top'
 * - `missing`  cosa fare se il bersaglio non c'e': 'dock' (predefinito) mostra il
 *              passo con la card in basso al centro e nessun foro, 'skip' lo salta
 * - `timeout`  quanto aspettare il bersaglio, in ms
 *
 * @param {Array|Function} source elenco dei passi, o funzione che lo costruisce al
 *   lancio del giro invece che al montaggio della pagina. Serve a chi ne mostra solo
 *   alcuni: quello che dipende da una chiamata, nel `setup()` non e' ancora arrivato.
 * @param {{ onStop?: Function }} [options] `onStop` gira sia alla fine sia
 *   sull'abbandono del giro: e' il punto in cui si rimette a posto la pagina.
 */
export function useTour(source, options = {}) {
	const active = ref(false);
	const busy = ref(false);
	const index = ref(0);
	const rect = ref(null);
	let frame = 0;
	let settleUntil = 0;
	let settleDeadline = 0;

	const steps = ref(typeof source === 'function' ? [] : source);

	const step = computed(() => (active.value ? steps.value[index.value] || null : null));
	const total = computed(() => steps.value.length);

	function resolve(item) {
		const found = typeof item.target === 'string' ? document.querySelector(item.target) : item.target();

		// Un elemento staccato o senza area occupata non si puo' evidenziare.
		return found && found.isConnected && found.getClientRects().length > 0 ? found : null;
	}

	function sameRect(a, b) {
		return Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
	}

	/** Restituisce `true` quando il rettangolo e' cambiato rispetto all'ultima lettura. */
	function measure() {
		const current = step.value;
		const element = current ? resolve(current) : null;

		if (!current || !element) {
			const had = rect.value !== null;

			rect.value = null;

			return had;
		}

		const box = element.getBoundingClientRect();
		const padding = typeof current.padding === 'number' ? current.padding : SPOT_PADDING;
		const next = {
			height: box.height + padding * 2,
			left: box.left - padding,
			top: box.top - padding,
			width: box.width + padding * 2,
		};

		if (!rect.value || !sameRect(rect.value, next)) {
			rect.value = next;

			return true;
		}

		return false;
	}

	/**
	 * Misura per un po' di frame di fila invece che di continuo: leggere la
	 * posizione costringe il browser a ricalcolare il foglio di stile, e su una
	 * pagina fitta farlo a ogni frame la inchioda.
	 *
	 * La finestra si riapre a ogni scorrimento, ridimensionamento o cambio di
	 * passo, cioe' le sole occasioni in cui l'elemento evidenziato si sposta; a
	 * riposo non gira nulla.
	 */
	function tick() {
		const now = performance.now();

		// Finche' il bersaglio si sposta la finestra si riapre: una transizione
		// lunga - la dialog a tutto schermo che sale - dura piu' di `SETTLE_MS`, e
		// fermarsi a meta' lascerebbe il ritaglio dove l'elemento non e' piu'.
		if (measure() && now + SETTLE_MS < settleDeadline) {
			settleUntil = now + SETTLE_MS;
		}

		if (now < settleUntil && now < settleDeadline) {
			frame = requestAnimationFrame(tick);

			return;
		}

		frame = 0;
	}

	function settle(duration = SETTLE_MS) {
		const now = performance.now();

		settleUntil = now + duration;
		settleDeadline = Math.max(settleDeadline, now + SETTLE_MAX_MS);

		if (!frame) {
			frame = requestAnimationFrame(tick);
		}
	}

	function onViewportChange() {
		settle(SETTLE_SCROLL_MS);
	}

	/**
	 * Attende che il bersaglio compaia, per esempio dopo che `before` ha cambiato
	 * la pagina. L'attesa fra un tentativo e l'altro non usa i frame di disegno:
	 * quando la scheda passa in secondo piano il browser smette di produrli, e il
	 * giro resterebbe fermo per sempre su un passo che invece va mostrato.
	 */
	async function waitForTarget(item) {
		const deadline = performance.now() + (typeof item.timeout === 'number' ? item.timeout : TARGET_TIMEOUT_MS);

		do {
			await nextTick();

			const element = resolve(item);

			if (element) {
				return element;
			}

			await new Promise(done => setTimeout(done, POLL_MS));
		} while (performance.now() < deadline);

		return null;
	}

	function scrollIfNeeded(element) {
		const box = element.getBoundingClientRect();
		const visible = box.top >= SCROLL_MARGIN && box.bottom <= window.innerHeight - SCROLL_MARGIN;

		if (!visible) {
			// Istantaneo: con lo scorrimento animato il ritaglio inseguirebbe la pagina.
			element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
		}
	}

	async function goTo(from, back) {
		busy.value = true;

		try {
			let cursor = from;

			while (cursor >= 0 && cursor < steps.value.length) {
				index.value = cursor;

				const current = steps.value[cursor];

				if (current.before) {
					await current.before({ back });
				}

				const element = await waitForTarget(current);

				if (element) {
					scrollIfNeeded(element);
					await nextTick();
					measure();
					settle();

					return;
				}

				// Bersaglio assente. Nei form a cascata del BO e' la norma - la meta'
				// della pagina sta dietro un `v-if` - e il passo va raccontato lo
				// stesso: la card si aggancia in basso al centro e il foro non c'e'.
				if (current.missing !== 'skip') {
					rect.value = null;
					settle();

					return;
				}

				// Solo chi lo chiede si fa saltare: si prosegue nella direzione di
				// marcia invece di lasciare il giro fermo su un passo muto.
				cursor += back ? -1 : 1;
			}

			await stop();
		} finally {
			busy.value = false;
		}
	}

	async function start() {
		if (active.value) {
			return;
		}

		if (typeof source === 'function') {
			steps.value = source();
		}

		if (!steps.value.length) {
			return;
		}

		active.value = true;
		index.value = 0;
		// In cattura, cosi' vale anche per gli scorrimenti dentro la pagina.
		document.addEventListener('scroll', onViewportChange, { capture: true, passive: true });
		window.addEventListener('resize', onViewportChange, { passive: true });
		await goTo(0, false);
	}

	async function stop() {
		if (!active.value) {
			return;
		}

		document.removeEventListener('scroll', onViewportChange, { capture: true });
		window.removeEventListener('resize', onViewportChange);
		cancelAnimationFrame(frame);
		frame = 0;
		settleUntil = 0;
		settleDeadline = 0;
		active.value = false;
		rect.value = null;

		if (options.onStop) {
			await options.onStop();
		}
	}

	async function next() {
		if (busy.value || !active.value) {
			return;
		}

		if (index.value >= steps.value.length - 1) {
			await stop();

			return;
		}

		await goTo(index.value + 1, false);
	}

	async function prev() {
		if (busy.value || !active.value || index.value === 0) {
			return;
		}

		await goTo(index.value - 1, true);
	}

	return {
		active,
		busy,
		index,
		next,
		prev,
		rect,
		start,
		step,
		stop,
		total,
	};
}
